/**
 * BookEditor sub-components — extracted to shrink the parent from 327 lines
 * to ~180 and give each section its own testable seam.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import {
  Upload, Sparkles, Wand2, Download, ImageIcon, ChevronLeft,
  Save, Loader2, FileText, Edit3, GripVertical, Trash2, Palette, ShieldCheck,
  MoveRight, AlertTriangle, X, Lock, Unlock, RefreshCw,
} from "lucide-react";
import { QAPanel } from "@/components/QAPanel";

// --------------------------------------------------------------------------
// StylePackSelector — lets the user pick which style pack the exporter
// applies. Fetches the list from /formatter-v2/style-packs once (cached).
// --------------------------------------------------------------------------
export function StylePackSelector({ value, onChange }) {
  const [packs, setPacks] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/formatter-v2/style-packs");
        if (!cancelled) setPacks(data.style_packs || []);
      } catch (err) { console.debug("style-pack list load failed:", err?.message); }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!packs.length) return null;
  const current = packs.find((p) => p.id === value) || packs[0];
  return (
    <div className="relative inline-flex items-center gap-1" data-testid="style-pack-selector">
      <Palette className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={1.5}/>
      <select value={value} onChange={(e)=>onChange(e.target.value)}
        className="text-xs border border-[color:var(--border)] bg-white px-2 py-1 pr-6 focus:outline-none focus:border-[color:var(--primary)]"
        data-testid="style-pack-select" title={current?.best_for || ""}>
        {packs.map((p) => (
          // Use a single interpolated string so React renders one text
          // node inside <option>. Prior form ``{p.name} — {p.trim}`` gave
          // three sibling nodes which the browser's visual editor could
          // wrap in a <span data-ve-dynamic>, breaking HTML validity
          // (option cannot contain span) and triggering hydration warnings
          // (tracked from iter-15 through iter-21).
          <option key={p.id} value={p.id}>{`${p.name} — ${p.trim}`}</option>
        ))}
      </select>
    </div>
  );
}

// --------------------------------------------------------------------------
// IntegrityBadge — surfaces the formatter_v2 integrity numbers so authors
// can see at a glance that their manuscript was ingested cleanly.
// --------------------------------------------------------------------------
function IntegrityBadge({ report }) {
  if (!report) return null;
  const clean = report.missing_blocks === 0 && report.duplicated_blocks === 0
    && report.sequence_violations === 0 && !report.export_blocked;
  const color = clean ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : "text-amber-800 bg-amber-50 border-amber-200";
  const label = clean
    ? `${report.chapter_count} ch · ${report.part_count} pt · ${report.rendered_blocks} blocks`
    : `${report.warnings?.length || 0} warning(s)`;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-xs ${color}`}
          data-testid="integrity-badge" title={JSON.stringify(report, null, 2)}>
      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5}/> {label}
    </span>
  );
}

// --------------------------------------------------------------------------
// ParserWarningsBanner (iter-35) — inline amber banner shown right under
// the ManuscriptCard when the formatter_v2 integrity report reports a
// "chapter(s) listed in the TOC but missing from the body" warning.
// Encourages the author to fix the DOCX and re-upload before spending
// Nano Banana quota on incomplete manuscripts. Dismissable per book +
// warning fingerprint via sessionStorage so a fresh upload re-shows it.
// --------------------------------------------------------------------------
export function ParserWarningsBanner({ bookId, report }) {
  // Extract only the missing-body-chapter warning (iter-29 wording lives in
  // /app/backend/formatter_v2/pipeline.py). Other warnings surface via the
  // integrity badge tooltip and the QA panel already.
  const missingLine = (report?.warnings || []).find(
    (w) => typeof w === "string" && w.includes("listed in the TOC but missing from the body"));

  // sessionStorage key includes the report fingerprint (set by server.py on
  // every upload) so any new upload — same book or not — resets the
  // dismissed state and re-shows the warning.
  const dismissKey = React.useMemo(
    () => (bookId && missingLine) ? `parser-warn-dismiss:${bookId}:${report?.fingerprint || ""}` : null,
    [bookId, missingLine, report?.fingerprint]);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined" || !dismissKey) return false;
    return window.sessionStorage.getItem(dismissKey) === "1";
  });
  // Reset the dismissed flag when the fingerprint changes (new upload).
  useEffect(() => {
    if (typeof window === "undefined" || !dismissKey) return;
    setDismissed(window.sessionStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);

  if (!missingLine || dismissed) return null;

  const dismiss = () => {
    if (dismissKey && typeof window !== "undefined") {
      window.sessionStorage.setItem(dismissKey, "1");
    }
    setDismissed(true);
  };

  return (
    <div
      className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-start gap-2"
      role="alert"
      data-testid="parser-warnings-banner"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5}/>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-amber-900" data-testid="parser-warnings-title">
          Some TOC chapters are missing from the body
        </p>
        <p className="mt-1 leading-relaxed" data-testid="parser-warnings-detail">
          {missingLine}
        </p>
        <p className="mt-1 text-[11px] text-amber-800">
          Fix your source document (apply a Heading style to each chapter
          opener paragraph) and re-upload before generating hero images —
          otherwise those chapters won't be picked up.
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-0.5 text-amber-800 hover:text-amber-900"
        aria-label="Dismiss warning"
        data-testid="parser-warnings-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={1.5}/>
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------
export function TopBar({ onExport, stylePack, setStylePack, report, bookId }) {
  return (
    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
      <Link to="/dashboard" className="link-underline text-sm inline-flex items-center gap-1" data-testid="back-to-library">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Library
      </Link>
      <div className="flex items-center gap-2 flex-wrap">
        <IntegrityBadge report={report}/>
        {setStylePack && <StylePackSelector value={stylePack} onChange={setStylePack}/>}
        {bookId && <QAPanel bookId={bookId} stylePack={stylePack}/>}
        <button onClick={()=>onExport("epub")} className="btn-ghost inline-flex items-center gap-2" data-testid="export-epub">
          <Download className="w-4 h-4" strokeWidth={1.5}/> Export EPUB
        </button>
        <button onClick={()=>onExport("docx")} className="btn-ghost inline-flex items-center gap-2" data-testid="export-docx">
          <Download className="w-4 h-4" strokeWidth={1.5}/> Export DOCX
        </button>
        <button onClick={()=>onExport("pdf")} className="btn-primary inline-flex items-center gap-2" data-testid="export-pdf">
          <Download className="w-4 h-4" strokeWidth={1.5}/> Export PDF
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
export function MetaCard({ book, setBook, patch, saving, aiBusy, aiBlurb, uploadCover }) {
  const coverRef = React.useRef(null);
  // iter-98 — Cover Focal Point. Click anywhere on the cover art to
  // pin a focal point so the title-page render never crops through
  // a face. Percentages 0-100 map to CSS `object-position`; empty
  // dict means "no focal point" (letterbox / contain).
  const fp = book?.cover_focal_point && typeof book.cover_focal_point === "object"
                ? book.cover_focal_point : {};
  const hasFocal = typeof fp.x === "number" && typeof fp.y === "number";
  const handleCoverClick = (e) => {
    if (!book.cover_data_url) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    const next = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    setBook({ ...book, cover_focal_point: next });
    patch({ cover_focal_point: next });
  };
  const clearFocal = () => {
    setBook({ ...book, cover_focal_point: {} });
    patch({ cover_focal_point: {} });
  };
  return (
    <div className="border border-[color:var(--border)] bg-white p-6 mb-6 grid grid-cols-12 gap-6" data-testid="book-meta">
      <div className="col-span-12 md:col-span-3">
        <div
          className={`aspect-[3/4] bg-[color:var(--canvas)] border border-[color:var(--border)] overflow-hidden flex items-center justify-center ${book.cover_data_url ? "cursor-crosshair" : ""} relative`}
          title={book.cover_data_url ? "Click to pin a focal point — the title-page render will crop around it" : undefined}
          onClick={book.cover_data_url ? handleCoverClick : undefined}
          data-testid="cover-focal-target"
        >
          {book.cover_data_url ? (
            <img src={book.cover_data_url}
                 className="w-full h-full"
                 style={{
                   objectFit: hasFocal ? "cover" : "cover",
                   objectPosition: hasFocal ? `${fp.x}% ${fp.y}%` : "center center",
                 }}
                 alt="cover"/>
          ) : (
            <div className="text-center p-4 text-[color:var(--text-muted)]">
              <ImageIcon className="w-8 h-8 mx-auto mb-2" strokeWidth={1.25}/>
              <p className="text-xs uppercase tracking-widest">No cover</p>
            </div>
          )}
          {hasFocal && book.cover_data_url && (
            <div className="absolute pointer-events-none"
                 style={{
                   left: `${fp.x}%`, top: `${fp.y}%`,
                   transform: "translate(-50%, -50%)",
                 }}
                 data-testid="cover-focal-dot"
                 aria-hidden>
              <div className="w-4 h-4 rounded-full bg-white border-2 border-[color:var(--primary)] shadow-md"/>
              <div className="absolute inset-0 w-4 h-4 rounded-full animate-ping bg-[color:var(--primary)] opacity-40"/>
            </div>
          )}
        </div>
        <input ref={coverRef} type="file" accept="image/*" className="hidden"
          onChange={(e)=>{ if(e.target.files?.[0]) uploadCover(e.target.files[0]); }} data-testid="cover-input"/>
        <button onClick={()=>coverRef.current?.click()} className="btn-ghost w-full mt-3 text-sm" data-testid="upload-cover-btn">
          {book.cover_data_url ? "Replace cover" : "Upload cover"}
        </button>
        {book.cover_data_url && (
          <div className="mt-2 text-[10px] text-[color:var(--text-muted)]" data-testid="cover-focal-hint">
            {hasFocal ? (
              <div className="flex items-center justify-between gap-2">
                <span>Focal · <span className="font-mono">{Math.round(fp.x)}% × {Math.round(fp.y)}%</span></span>
                <button onClick={clearFocal}
                        className="underline hover:text-[color:var(--primary)]"
                        data-testid="cover-focal-clear">Clear</button>
              </div>
            ) : (
              <span>Click the cover to pin a focal point so the title-page never crops through a face.</span>
            )}
          </div>
        )}
      </div>
      <div className="col-span-12 md:col-span-9 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-widest mb-1">Title</span>
            <input value={book.title} onChange={(e)=>setBook({...book, title: e.target.value})}
              onBlur={(e)=>patch({title: e.target.value})}
              className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]" data-testid="meta-title"/>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest mb-1">Author</span>
            <input value={book.author} onChange={(e)=>setBook({...book, author: e.target.value})}
              onBlur={(e)=>patch({author: e.target.value})}
              className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]" data-testid="meta-author"/>
          </label>
        </div>
        <label className="block">
          <div className="flex items-center justify-between mb-1">
            <span className="block text-xs uppercase tracking-widest">Description / Blurb</span>
            <button onClick={aiBlurb} disabled={aiBusy==="blurb"} className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:text-[color:var(--primary-hover)]" data-testid="ai-blurb-btn">
              {aiBusy==="blurb" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} AI generate
            </button>
          </div>
          <textarea rows={3} value={book.description || ""}
            onChange={(e)=>setBook({...book, description: e.target.value})}
            onBlur={(e)=>patch({description: e.target.value})}
            className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)] font-reading" data-testid="meta-description"/>
        </label>
        {saving && <p className="text-xs text-[color:var(--text-muted)] flex items-center gap-1"><Save className="w-3 h-3"/> Saving…</p>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
export function ManuscriptCard({ book, fileRef, uploadManuscript, openManualCompose,
                                 autoGenerateHeroes, setAutoGenerateHeroes }) {
  // iter-31: guard the re-upload if the user has WYSIWYG chapter edits —
  // they will be wiped because the DOCX re-parse rebuilds every
  // source_index from scratch.
  const overrideCount = Object.keys(book?.chapter_html_overrides || {}).length;
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (overrideCount > 0) {
      const ok = window.confirm(
        `You have hand-edited ${overrideCount} chapter(s) via the WYSIWYG editor. `
        + `Re-uploading a manuscript will delete those edits — the parser recomputes `
        + `every chapter from scratch, so overrides can no longer be attached.\n\n`
        + `Continue with the re-upload?`
      );
      if (!ok) { e.target.value = ""; return; }
    }
    uploadManuscript(file);
  };
  return (
    <div className="bg-white border border-[color:var(--border)] p-5">
      <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-3">§ Manuscript</p>
      <input ref={fileRef} type="file" accept=".txt,.md,.markdown,.docx,.pdf" className="hidden"
        onChange={handleFileChange} data-testid="manuscript-input"/>
      <button onClick={()=>fileRef.current?.click()} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="upload-manuscript-btn">
        <Upload className="w-4 h-4" strokeWidth={1.5}/> Upload draft
      </button>
      {/* iter-34: opt-in auto-generation of hero images for every chapter
          right after the DOCX is imported. Off by default so users
          don't burn their daily Nano Banana quota unintentionally. */}
      {setAutoGenerateHeroes && (
        <label className="flex items-start gap-2 mt-3 text-xs cursor-pointer"
               data-testid="auto-generate-heroes-toggle">
          <input
            type="checkbox"
            checked={!!autoGenerateHeroes}
            onChange={(e) => setAutoGenerateHeroes(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-[color:var(--text-muted)]">
            <span className="font-medium text-[color:var(--text)]">
              Also generate hero images for every chapter
            </span>
            <span className="block mt-0.5">
              Kicks off batch Nano Banana right after import. You can edit the
              shared prompt template before it starts. Skip if you want to save
              your daily image quota.
            </span>
          </span>
        </label>
      )}
      <p className="text-xs text-[color:var(--text-muted)] mt-2">
        .txt · .docx · .md · .pdf (max 20MB)
        {overrideCount > 0 && (
          <span className="text-amber-800 block mt-1" data-testid="upload-override-warning">
            {overrideCount} chapter{overrideCount === 1 ? " has" : "s have"} WYSIWYG edits — re-uploading will delete them.
          </span>
        )}
      </p>
      <div className="border-t border-[color:var(--border)] mt-4 pt-3">
        <p className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">§ or paste field-by-field</p>
        <button onClick={openManualCompose} className="btn-ghost w-full inline-flex items-center justify-center gap-2 text-sm"
          data-testid="open-manual-compose">
          <Edit3 className="w-4 h-4" strokeWidth={1.5}/> Manual entry
        </button>
        <p className="text-xs text-[color:var(--text-muted)] mt-2">
          Front matter, introduction, chapters, back matter, resources — no file needed.
        </p>
        {(book?.introduction || (book?.front_matter?.length || 0) > 0
          || (book?.back_matter?.length || 0) > 0 || (book?.resources?.length || 0) > 0) && (
          <div className="text-xs mt-2 space-y-0.5" data-testid="manual-compose-summary">
            {book.introduction && <div>✓ Introduction saved</div>}
            {(book.front_matter?.length || 0) > 0 && <div>✓ {book.front_matter.length} front-matter item(s)</div>}
            {(book.back_matter?.length || 0) > 0 && <div>✓ {book.back_matter.length} back-matter item(s)</div>}
            {(book.resources?.length || 0) > 0 && <div>✓ {book.resources.length} resource(s)</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
export function AiToolsCard({ book, aiBusy, aiDetect, aiFixTypos }) {
  return (
    <div className="bg-white border border-[color:var(--border)] p-5 space-y-3">
      <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ AI tools</p>
      <button onClick={aiDetect} disabled={!book.chapters?.length || aiBusy==="detect"} className="btn-ghost w-full inline-flex items-center justify-center gap-2 text-sm" data-testid="ai-detect-btn">
        {aiBusy==="detect" ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4" strokeWidth={1.5}/>} Re-detect chapters
      </button>
      <button onClick={aiFixTypos} disabled={!book.chapters?.length || aiBusy==="typos"} className="btn-ghost w-full inline-flex items-center justify-center gap-2 text-sm" data-testid="ai-typos-btn">
        {aiBusy==="typos" ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4" strokeWidth={1.5}/>} Fix typos (current chapter)
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------
// ChapterList — sidebar Contents panel with:
//   - click a chapter to select it
//   - inline edit the chapter title (pencil icon)
//   - HTML5 drag-to-reorder (grip handle)
//   - delete a chapter (trash icon)
// Callers pass `onReorder(from, to)`, `onRename(idx, newTitle)`, and
// `onDelete(idx)`. When these are omitted (backwards-compat), the list
// falls back to a read-only picker like the original.
export function ChapterList({ chapters, activeIdx, setActiveIdx, onReorder, onRename, onDelete, onReclassify, lockedIndices, onToggleLock }) {
  const [dragIdx, setDragIdx] = React.useState(null);
  const [dragOverIdx, setDragOverIdx] = React.useState(null);
  const [editIdx, setEditIdx] = React.useState(null);
  const [editValue, setEditValue] = React.useState("");
  const [menuIdx, setMenuIdx] = React.useState(null);

  // iter-79: chapter-aware sidebar numbering. Sequential position
  // indices (01, 02, 03…) confused users because a row labeled "PART 1"
  // was shown as 01 while the real "Chapter 1" landed on 02. Now only
  // rows with _kind === "chapter" (or undefined, i.e. legacy) consume
  // the counter; Parts show "§", other reclassified rows show "·".
  const rowMarkers = React.useMemo(() => {
    let n = 0;
    return (chapters || []).map((c) => {
      const kind = (c?._kind || "chapter").toLowerCase();
      if (kind === "chapter") {
        n += 1;
        return { label: String(n).padStart(2, "0"), kind };
      }
      if (kind === "part") return { label: "§", kind };
      return { label: "·", kind };
    });
  }, [chapters]);

  if (!chapters?.length) return null;
  const isLocked = (c) => {
    if (!lockedIndices) return false;
    const idx = c.chapter_source_index;
    return idx != null && lockedIndices.has(Number(idx));
  };

  const startEdit = (i) => { setEditIdx(i); setEditValue(chapters[i].title || ""); };
  const commitEdit = () => {
    if (editIdx !== null && onRename) onRename(editIdx, editValue.trim() || `Chapter ${editIdx + 1}`);
    setEditIdx(null);
  };

  return (
    <div className="bg-white border border-[color:var(--border)] p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Contents ({chapters.length})</p>
        {onReorder && (
          <span className="text-[10px] text-[color:var(--text-muted)]" title="Drag the grip handle on the left to reorder">
            drag to reorder
          </span>
        )}
      </div>
      <ul className="max-h-[420px] overflow-y-auto -mx-2" data-testid="chapter-list">
        {chapters.map((c, i) => {
          const isDragging = dragIdx === i;
          const isDropTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
          return (
            <li
              key={c.id || `${c.title}-${i}`}
              className={`transition-opacity ${isDropTarget ? "border-t-2 border-t-[color:var(--primary)]" : ""} ${isDragging ? "opacity-40" : ""}`}
              onDragOver={onReorder ? (e)=>{ if(!isDragging){ e.preventDefault(); e.dataTransfer.dropEffect="move"; } } : undefined}
              onDragEnter={onReorder ? ()=>{ if(dragIdx !== null) setDragOverIdx(i); } : undefined}
              onDrop={onReorder ? (e)=>{
                e.preventDefault();
                if (dragIdx !== null && dragIdx !== i) onReorder(dragIdx, i);
                setDragIdx(null); setDragOverIdx(null);
              } : undefined}
            >
              <div className={`group flex items-center gap-1 px-2 py-1.5 border-l-2 text-sm ${
                i===activeIdx
                  ? "border-[color:var(--primary)] bg-[color:var(--canvas)] font-medium"
                  : "border-transparent hover:bg-[color:var(--canvas)]/60"
              }`}>
                {onReorder && (
                  <span
                    draggable
                    onDragStart={(e)=>{
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", String(i)); }
                      catch (err) { console.debug("dataTransfer.setData blocked:", err?.message); }
                      setDragIdx(i);
                    }}
                    onDragEnd={()=>{ setDragIdx(null); setDragOverIdx(null); }}
                    className="cursor-grab active:cursor-grabbing p-0.5 text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                    title="Drag to reorder"
                    data-testid={`chapter-drag-${i}`}
                  >
                    <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  </span>
                )}
                <span className="text-[color:var(--text-muted)] w-6 text-xs font-mono"
                        title={rowMarkers[i]?.kind === "part"
                                ? "Part — does not consume a chapter number"
                                : rowMarkers[i]?.kind === "chapter"
                                ? "Chapter number"
                                : `Reclassified as ${(rowMarkers[i]?.kind || "").replace(/_/g," ")}`}>
                  {rowMarkers[i]?.label || String(i+1).padStart(2,"0")}
                </span>
                {editIdx === i ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={(e)=>setEditValue(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e)=>{ if(e.key === "Enter") commitEdit(); if(e.key === "Escape") setEditIdx(null); }}
                    className="flex-1 min-w-0 border border-[color:var(--border)] px-1 py-0.5 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid={`chapter-title-input-${i}`}
                  />
                ) : (
                  <button
                    onClick={()=>setActiveIdx(i)}
                    className={
                      "flex-1 min-w-0 text-left truncate "
                      + (i === activeIdx
                          ? "font-semibold text-[color:var(--primary)] "
                          : "text-[color:var(--text)] ")
                    }
                    aria-current={i === activeIdx ? "true" : undefined}
                    data-testid={`chapter-item-${i}`}
                    data-active={i === activeIdx ? "true" : "false"}
                    title={
                      c._kind && c._kind !== "chapter" && c._kind !== "part"
                        ? `${c.title} — Reclassified as ${c._kind.replace(/_/g," ")}`
                        : c.title
                    }
                  >
                    {c.title}
                    {c._kind && c._kind !== "chapter" && c._kind !== "part" && (
                      /* iter-44: reclassify badge so authors know why the
                         WYSIWYG "Edit chapter" button is disabled for this
                         row (moved to front/back matter via the Structure
                         editor). */
                      <span
                        className="ml-2 inline-block text-[9px] px-1.5 py-0 border border-amber-400 text-amber-700 uppercase tracking-widest align-middle"
                        data-testid={`chapter-reclassified-badge-${i}`}
                        data-kind={c._kind}
                      >
                        {c._kind === "front_matter" ? "Front" : c._kind === "back_matter" ? "Back" : c._kind}
                      </span>
                    )}
                    {isLocked(c) && (
                      /* iter-65 — inline padlock indicator on locked chapters/TOC entries. */
                      <Lock
                        className="ml-2 inline-block w-3 h-3 align-middle text-[color:var(--primary)]"
                        strokeWidth={2}
                        data-testid={`chapter-locked-badge-${i}`}
                      />
                    )}
                  </button>
                )}
                {onToggleLock && c.chapter_source_index != null && (
                  /* iter-66 — per-chapter Lock/Unlock button in the TOC sidebar. */
                  <button
                    onClick={() => onToggleLock(c.chapter_source_index, !isLocked(c))}
                    className={`p-0.5 opacity-70 hover:opacity-100 transition-colors ${
                      isLocked(c)
                        ? "text-[color:var(--primary)]"
                        : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                    }`}
                    title={isLocked(c) ? "Unlock — allow role changes and reorder" : "Lock — pin this chapter in place"}
                    data-testid={`chapter-lock-toggle-${i}`}
                  >
                    {isLocked(c)
                      ? <Lock className="w-3.5 h-3.5" strokeWidth={2}/>
                      : <Unlock className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                  </button>
                )}
                {onRename && editIdx !== i && (
                  <button
                    onClick={()=>startEdit(i)}
                    className="p-0.5 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"
                    title="Rename chapter"
                    data-testid={`chapter-rename-${i}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  </button>
                )}
                {onReclassify && (
                  <div className="relative">
                    <button
                      onClick={()=>setMenuIdx(menuIdx === i ? null : i)}
                      className="p-0.5 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"
                      title="Reclassify chapter (front / back matter)"
                      data-testid={`chapter-reclassify-${i}`}
                    >
                      <MoveRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    </button>
                    {menuIdx === i && (
                      <div
                        className="absolute right-0 top-full mt-1 z-20 min-w-[190px] bg-white border border-[color:var(--border)] shadow-lg"
                        data-testid={`chapter-reclassify-menu-${i}`}
                        onMouseLeave={()=>setMenuIdx(null)}
                      >
                        <div className="p-1">
                          <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 py-1">
                            Move this chapter to…
                          </p>
                          <button
                            onClick={()=>{ onReclassify(i, "front_matter"); setMenuIdx(null); }}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-[color:var(--canvas)]"
                            data-testid={`chapter-reclassify-${i}-front`}
                          >
                            Front matter
                          </button>
                          <button
                            onClick={()=>{ onReclassify(i, "back_matter"); setMenuIdx(null); }}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-[color:var(--canvas)]"
                            data-testid={`chapter-reclassify-${i}-back`}
                          >
                            Back matter
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {onDelete && (
                  <button
                    onClick={()=>{ if (window.confirm(`Delete "${c.title}"?`)) onDelete(i); }}
                    className="p-0.5 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-red-600"
                    title="Delete chapter"
                    data-testid={`chapter-delete-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------
// iter-31: whitelist for the chapter preview HTML injection. Mirrors
// the WYSIWYG editor's whitelist so any content the editor produces
// round-trips through the preview safely. DOMPurify strips anything not
// on this list; the server-side sanitizer runs at save-time too.
const PREVIEW_ALLOWED_TAGS = [
  "p", "br", "span", "div",
  "strong", "b", "em", "i", "u", "s", "sub", "sup",
  "h2", "h3", "h4",
  "ul", "ol", "li",
  "a",
  "blockquote", "code", "pre",
  "hr",
  "img", "figure", "figcaption",
  "table", "thead", "tbody", "tr", "th", "td",
];
const PREVIEW_ALLOWED_ATTRS = [
  "href", "title", "rel", "target", "src", "alt", "width", "height",
  "colspan", "rowspan", "class",
];

export function ChapterPreview({ book, activeChapter, activeIdx }) {  if (!book.chapters?.length) {
    return (
      <div className="text-center py-20 text-[color:var(--text-muted)]" data-testid="no-manuscript">
        <FileText className="w-10 h-10 mx-auto mb-4" strokeWidth={1.25}/>
        <p className="font-editorial text-3xl text-[color:var(--text)]">No manuscript yet</p>
        <p className="mt-2 max-w-md mx-auto">Upload a draft to begin formatting. We'll detect chapters and give you a live preview.</p>
      </div>
    );
  }
  return (
    <div className="paper-page mx-auto max-w-[720px] p-10 md:p-16 min-h-[65vh]" data-testid="preview-pane">
      <p className="text-center text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] mb-6">
        {book.title}
      </p>
      <h1 className="font-editorial text-4xl md:text-5xl text-center leading-tight mb-10">
        {activeChapter.title}
      </h1>
      {activeChapter.hero_image && (
        <figure className="mb-10" data-testid="chapter-preview-hero">
          <img
            src={activeChapter.hero_image}
            alt=""
            className="mx-auto block max-h-72 w-auto rounded shadow-md"
            style={{ maxWidth: "100%", objectFit: "cover" }}
          />
        </figure>
      )}
      <div className="font-reading text-[1.02rem] leading-[1.75] text-[color:var(--text)]">
        {(activeChapter.has_html_override
          || /^\s*<(?:p|h[1-6]|ul|ol|div|table|blockquote|section|figure|pre|hr)\b/i.test(activeChapter.content || "")) ? (
          // iter-31: content is pre-rendered WYSIWYG HTML — inject
          // directly (sanitized on save server-side + client-side, and
          // re-sanitized here defense-in-depth).
          // iter-91 — DOCX-uploaded chapters keep HTML in `content` even
          // without an explicit override flag; auto-detect leading tags
          // so the reader doesn't see raw <p class="body"> markup.
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(activeChapter.content, {
                ALLOWED_TAGS: PREVIEW_ALLOWED_TAGS,
                ALLOWED_ATTR: PREVIEW_ALLOWED_ATTRS,
              }),
            }}
            className="wysiwyg-preview"
            data-testid="chapter-preview-html"
          />
        ) : (
          activeChapter.content.split(/\n{2,}/).map((p, i) => (
            <p key={`${activeIdx}-p-${i}`} style={{ textIndent: i===0 ? 0 : "1.2em", marginBottom: "0.7em", textAlign: "justify" }}>
              {p.split("\n").map((line, j, arr) => (
                <React.Fragment key={`${activeIdx}-p-${i}-${j}`}>{line}{j<arr.length-1 && <br/>}</React.Fragment>
              ))}
            </p>
          ))
        )}
      </div>
      <div className="mt-12 text-center text-xs text-[color:var(--text-muted)]">
        — {activeIdx+1} / {book.chapters.length} —
      </div>
    </div>
  );
}
